/**
 * SimplePay test prompt of 3 November 2026, §2: emails about a test order
 * (`orders.is_test`, carried on the payload as `is_test`).
 *
 *   - *[TESZT]* before the subject, on every audience and every environment;
 *   - one line in the body, first, in the email's language:
 *     *Ez egy teszt rendelés: számla nem készül, a konyha nem kap feladatot.*
 *     / *This is a test order: no invoice is issued and nothing goes to the
 *     kitchen.*
 *   - any sentence about an invoice or a díjbekérő is replaced by that line,
 *     and a facts block that would show an invoice's number is dropped, since
 *     no document exists or will.
 *
 * Who receives a staff email about a test order is the database's decision
 * (`enqueue_outbox` sends it to `admin` only); this module only words it.
 */

import type { Block, Email } from './layout.ts'
import type { EmailPayload } from './payload.ts'

export const TEST_ORDER_SUBJECT_PREFIX = '[TESZT] '

export const TEST_ORDER_LINE = {
  hu: 'Ez egy teszt rendelés: számla nem készül, a konyha nem kap feladatot.',
  en: 'This is a test order: no invoice is issued and nothing goes to the kitchen.',
} as const

/** A sentence about a document this order will never have. */
const INVOICE_WORDS = /sz[áa]ml[áa]|díjbekérő|dijbekero|invoice|payment request|pro-?forma/i

const blockText = (block: Block): string | null =>
  block.kind === 'paragraph' || block.kind === 'note' || block.kind === 'band' ? block.text : null

export const isTestPayload = (payload: EmailPayload): boolean =>
  payload.is_test === true || payload.order?.is_test === true

export function testOrderEmail(email: Email, payload: EmailPayload): Email {
  if (!isTestPayload(payload)) return email
  const line = payload.lang === 'en' && payload.audience === 'customer' ? TEST_ORDER_LINE.en : TEST_ORDER_LINE.hu
  const blocks: Block[] = [{ kind: 'band', text: line, tone: 'warn' }]
  for (const block of email.blocks) {
    const text = blockText(block)
    if (text !== null && INVOICE_WORDS.test(text)) continue
    if (block.kind === 'facts' && block.rows.some(([label]) => INVOICE_WORDS.test(label))) continue
    blocks.push(block)
  }
  const subject = email.subject.startsWith(TEST_ORDER_SUBJECT_PREFIX)
    ? email.subject
    : TEST_ORDER_SUBJECT_PREFIX + email.subject
  return { ...email, subject, blocks }
}
